import { Link } from 'react-router-dom';
import type { Ticket } from '../types';
import { activityLabel, isStockReview, mobileTab, workPhase } from '../lib/workflow';
import { ServerTimer } from './ServerTimer';

const TYPE_META: Record<string, { label: string; icon: string; tone: string }> = {
  RECLAMATIE: { label: 'Reclamație', icon: '🔴', tone: '#dc2626' },
  PREVENTIE: { label: 'Prevenție', icon: '🟣', tone: '#7c3aed' },
  MODIFICARI: { label: 'Modificări', icon: '🔵', tone: '#2563eb' },
  INSTALARE: { label: 'Service', icon: '🔧', tone: '#ea580c' },
  SERVICE: { label: 'Service', icon: '🔧', tone: '#ea580c' },
  REVIZIE_DEPOZIT: { label: 'Revizie în depozit', icon: '🔧', tone: '#ea580c' },
};

function relativeDate(value?: string): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const days = Math.floor((Date.now() - date.getTime()) / 86_400_000);
  if (days <= 0) return 'astăzi';
  if (days === 1) return 'ieri';
  if (days < 7) return `acum ${days} zile`;
  return new Intl.DateTimeFormat('ro-RO', { day: 'numeric', month: 'short' }).format(date);
}

function equipmentLabel(ticket: Ticket): string {
  const code = String(ticket.deviceCode || '').trim();
  const name = String(ticket.deviceName || '').trim();
  if (!code) return name;
  if (!name || name.toLocaleLowerCase('ro-RO').includes(code.toLocaleLowerCase('ro-RO'))) return name || code;
  return `${code} ${name}`;
}

export function TicketCard({ ticket }: { ticket: Ticket }) {
  const tab = mobileTab(ticket);
  const phase = workPhase(ticket);
  const params = new URLSearchParams();
  if (ticket.runId) params.set('runId', ticket.runId);
  const suffix = params.size ? `?${params}` : '';
  const typeKey = String(ticket.type || '').toUpperCase();
  const type = TYPE_META[typeKey] || {
    label: phase === 'service' ? 'Service' : phase === 'field' ? 'Teren' : 'Tichet',
    icon: phase === 'service' ? '🔧' : '🔵',
    tone: phase === 'service' ? '#ea580c' : '#2563eb',
  };
  const title = String(ticket.locationName || '').trim()
    || (typeKey === 'RECLAMATIE' ? 'Reclamație' : `Tichet #${ticket.ticketId}`);
  const subtitle = String(ticket.locationAddress || '').trim();
  const equipment = equipmentLabel(ticket);

  return <Link className={`ticket-card dashboard-ticket-card ${ticket.unreadAssigned ? 'unread' : ''}`} to={`/tickets/${encodeURIComponent(ticket.ticketId)}${suffix}`}>
    <div className="ticket-card__top">
      <div className="ticket-card__badges">
        <span className={`status status-${tab}`}>{tab === 'new' ? '● Nou' : tab === 'in_progress' ? '● În lucru' : isStockReview(ticket) ? 'Revizionat' : 'Finalizat'}</span>
        <span className="type-badge" style={{ color: type.tone, background: `${type.tone}14` }}>{type.icon} {phase ? activityLabel(ticket) : type.label}</span>
      </div>
      <span className="ticket-card__date">{relativeDate(ticket.assignedAt || ticket.createdAt)}</span>
    </div>
    <h3>{title}</h3>
    {subtitle && <p className="ticket-card__subtitle">{subtitle}</p>}
    {equipment && <p className="ticket-card__equipment">{equipment}</p>}
    <p className="ticket-card__description">{ticket.description || '—'}</p>
    {tab === 'in_progress' && <div className="ticket-card__timer"><span>În lucru</span><strong><ServerTimer ticket={ticket} /></strong></div>}
    <div className="ticket-card__foot"><span>#{ticket.ticketId.slice(-6).toUpperCase()}</span><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 6 6 6-6 6" /></svg></div>
  </Link>;
}
